import { portalText, portalLocale } from "../../i18n/portal.js";
/**
 * New campaign's pure rules for the design export of 2026-10-09: the name check, the two choices for
 * people already surveyed, the four waits with their one-line descriptions, the days sentence, the
 * "When it runs" dates, and the plan the estimate panel prints (first calls done by, last retry by).
 *
 * Kept apart from the screen so each rule is testable without mounting anything.
 *
 * THE PLAN IS AN ESTIMATE, AND SAYS SO. The number of calling days the first calls take is the API's
 * (`CampaignEstimateView.dayCount`); this file only lays those days onto the calendar the member chose
 * — their calling days and hours, from the start they chose — and adds the retries the same way, as
 * the prototype does. It reads the browser's clock, so a date is the member's, never a promise per
 * person: each person is still only called inside the calling hours in their own time zone.
 *
 * AMENDED 2026-10-09 (plan-015 CMP, G16, G17, owner answers 5 and 6): the start and the end are each person's own
 * — the API reads `start_local_date` / `start_local_time` / `end_local_date` on every contact's clock — so the start
 * note is the design's ("Starts <day> at <time>, in each person's local time. Editable until then."), "Ends" is a
 * date (optional) with the design's end note ({@link endNoteOf}), and the dates convert to the API's `YYYY-MM-DD`.
 * The name check moved to the shared rule (plan-015 NAM, `campaignNameCheckOf` beside the screen), so this file's
 * copy of it is gone.
 */
import type { SegmentedOption } from "@wetalk/ui";

import type { CallingRangeView, ReturningPersonTreatmentView } from "../../api/gateway.js";

import { callingHoursProblemText, minuteOf, rangesOfDay, type MinuteRange } from "./calling-hours.js";

// -------------------------------------------------------------------------------------------
// Campaign name
// -------------------------------------------------------------------------------------------

export const CAMPAIGN_NAME_TIP =
  () => (portalText("operations", "3_to_60_characters_letters_in_any_language_numbers_spaces_8da143e9"));

// -------------------------------------------------------------------------------------------
// People already surveyed
// -------------------------------------------------------------------------------------------

/** The design's two choices. ("Different questions" is no longer drawn.) */
export const REPEAT_OPTION: readonly SegmentedOption<ReturningPersonTreatmentView>[] = [
  { value: "same_questions", get label() { return portalText("operations", "ask_them_again_dfe3516a"); } },
  { value: "skip_them", get label() { return portalText("operations", "skip_them_0572ee86"); } },
];

export function repeatNoteOf(treatment: ReturningPersonTreatmentView): string {
  return treatment === "skip_them"
    ? portalText("operations", "anyone_with_a_survey_already_on_file_from_this_agent_is_le_e5ec31e5")
    : portalText("operations", "they_hear_the_same_questions_again_their_earlier_answers_s_60f16e79");
}

// -------------------------------------------------------------------------------------------
// Waits, days and the tooltips
// -------------------------------------------------------------------------------------------

export const WAIT_CHOICE: readonly { readonly minute: number; readonly label: string; readonly dek: string }[] = [
  { minute: 30, get label() { return portalText("operations", "30_minutes_31bbe889"); }, get dek() { return portalText("operations", "same_session_a3f55bb9"); } },
  { minute: 240, get label() { return portalText("operations", "4_hours_7b47150d"); }, get dek() { return portalText("operations", "later_that_day_d353e690"); } },
  { minute: 1_440, get label() { return portalText("operations", "1_day_2d8a6f7c"); }, get dek() { return portalText("operations", "next_day_38f859dd"); } },
  { minute: 4_320, get label() { return portalText("operations", "3_days_09ad9df4"); }, get dek() { return portalText("operations", "later_in_the_week_4044c5ea"); } },
];

export const WAIT_TIP =
  () => (portalText("operations", "retries_only_go_out_on_your_calling_days_and_inside_your_c_e1bc7883"));

export const TIME_ZONE_TIP =
  () => (portalText("operations", "worked_out_from_each_number_s_country_code_countries_with_fde8f786"));

const DAY_NAME = () => ([portalText("operations", "mon_24b2a099"), portalText("operations", "tue_529541bb"), portalText("operations", "wed_23408b19"), portalText("operations", "thu_3593ccd9"), portalText("operations", "fri_bbd6e32e"), portalText("operations", "sat_6b782d41"), portalText("operations", "sun_48c98cab")]);

/** ISO weekday (Mon = 1 … Sun = 7) → "Mon". */
export function dayNameOf(isoDay: number): string {
  return DAY_NAME()[isoDay - 1] ?? String(isoDay);
}

/** "Calling weekdays · not on Sat, Sun", "Calling Mon, Wed · not on …", "… · every day". */
export function daysNoteOf(dayOfWeek: readonly number[]): string {
  const on = [1, 2, 3, 4, 5, 6, 7].filter((one) => dayOfWeek.includes(one)).map(dayNameOf);
  const off = [1, 2, 3, 4, 5, 6, 7].filter((one) => !dayOfWeek.includes(one)).map(dayNameOf);
  if (on.length === 0) return portalText("operations", "no_days_picked_the_campaign_will_not_call_anyone_4825f5e2");
  const named = on.join(", ") === "Mon, Tue, Wed, Thu, Fri" ? "weekdays" : on.join(", ");
  return portalText("operations", "calling_valuevalue_adb7a7f0", { v0: String(named), v1: String(off.length === 0 ? portalText("operations", "every_day_546dcb01") : portalText("operations", "not_on_value_b708a393", { v0: String(off.join(", ")) })) });
}

// -------------------------------------------------------------------------------------------
// Dates
// -------------------------------------------------------------------------------------------

const LOCAL_TIME = /^([01][0-9]|2[0-3]):[0-5][0-9]$/;

/** `DD/MM/YYYY` (one-digit day and month allowed) → the date it names, at midnight, or `null`. */
export function dateOfDmy(text: string): Date | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim());
  if (match === null) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
}

/** plan-015 CMP: `DD/MM/YYYY` as the API's `YYYY-MM-DD`, or `null` while it is unreadable. */
export function isoDateOfDmy(text: string): string | null {
  const date = dateOfDmy(text);
  if (date === null) return null;
  return `${String(date.getFullYear())}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** plan-015 CMP: the API's `YYYY-MM-DD` as `DD/MM/YYYY` (an edit opens on the stored dates). */
export function dmyOfIsoDate(iso: string): string {
  const [year, month, day] = iso.split("-");
  return `${day ?? ""}/${month ?? ""}/${year ?? ""}`;
}

/** A date as `DD/MM/YYYY`. */
export function dmyOf(date: Date): string {
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${String(date.getFullYear())}`;
}

/** Minutes past midnight → `HH:MM` (1440 is "24:00"). */
function clockOf(minute: number): string {
  return minute >= 1440 ? "24:00" : `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(minute % 60).padStart(2, "0")}`;
}

/** The first range's start on the next calling day after `isoDay` (a week ahead at most); `null` with none. */
function nextCallingDayFirstOf(hours: readonly CallingRangeView[], isoDay: number): number | null {
  for (let step = 1; step <= 7; step += 1) {
    const first = rangesOfDay(hours, ((isoDay - 1 + step) % 7) + 1)[0];
    if (first !== undefined) return first.from;
  }
  return null;
}

function at(date: Date, minute: number): Date {
  const next = new Date(date);
  next.setHours(Math.floor(minute / 60), minute % 60, 0, 0);
  return next;
}

function isoDayOf(date: Date): number {
  return date.getDay() === 0 ? 7 : date.getDay();
}

/** The start the member chose, as a moment on their clock, or `null` while either part is unreadable. */
export function startMomentOf(dateText: string, time: string): Date | null {
  const date = dateOfDmy(dateText);
  if (date === null || !LOCAL_TIME.test(time)) return null;
  return at(date, minuteOf(time));
}

/**
 * The draft's `starts_at`: the chosen moment, or `null` ("as soon as it can") when that moment has
 * already come — a start in the past means now.
 */
export function startsAtOfMoment(moment: Date | null, now: Date): string | null {
  if (moment === null || moment.getTime() <= now.getTime()) return null;
  return moment.toISOString();
}

/** "Tue 13 Oct". */
export function dayTextOf(date: Date): string {
  return `${dayNameOf(isoDayOf(date))} ${String(date.getDate())} ${date.toLocaleDateString((portalLocale() === "el" ? "el-GR" : "en-GB"), { month: "short" })}`;
}

export interface StartNote {
  readonly text: string;
  readonly warning: boolean;
}

/**
 * The sentence under "When it runs" about the start: what the chosen start means, or why the first
 * call will come later than it says.
 */
export function startNoteOf(input: {
  readonly dateText: string;
  readonly time: string;
  /** 2026-10-30: the calling ranges per weekday. */
  readonly callingHours: readonly CallingRangeView[];
  readonly now: Date;
}): StartNote {
  const date = dateOfDmy(input.dateText);
  if (date === null) return { get text() { return portalText("operations", "write_the_start_date_as_dd_mm_yyyy_dd077994"); }, warning: true };
  if (!LOCAL_TIME.test(input.time)) return { get text() { return portalText("operations", "write_the_first_call_as_hh_mm_in_24_hour_time_82a368d9"); }, warning: true };
  const today = at(input.now, 0);
  if (date.getTime() < today.getTime()) return { get text() { return portalText("operations", "that_date_has_passed_pick_today_or_later_95ce6b6c"); }, warning: true };
  const moment = at(date, minuteOf(input.time));
  if (moment.getTime() <= input.now.getTime()) {
    return { get text() { return portalText("operations", "the_first_calls_go_out_as_soon_as_you_start_inside_the_cal_53a71e8c"); }, warning: false };
  }
  const dayName = dayNameOf(isoDayOf(date));
  const dayRange = rangesOfDay(input.callingHours, isoDayOf(date));
  if (dayRange.length === 0) {
    return { get text() { return portalText("operations", "value_is_not_one_of_your_calling_days_so_the_first_call_wi_2159dd4b", { v0: String(dayName) }); }, warning: true };
  }
  // 2026-10-30: inside one of the day's ranges, or the first call waits for the next range (that day, or the next
  // calling day's first).
  const minute = minuteOf(input.time);
  if (!dayRange.some((one) => minute >= one.from && minute < one.to)) {
    const later = dayRange.find((one) => one.from > minute);
    const nextDayFirst = nextCallingDayFirstOf(input.callingHours, isoDayOf(date));
    const waits =
      later !== undefined
        ? clockOf(later.from)
        : portalText("operations", "the_next_calling_day_at_value_b96004fc", { v0: String(nextDayFirst === null ? "" : clockOf(nextDayFirst)) });
    const only = dayRange.length === 1 ? dayRange[0] : undefined;
    return {
      get text() {
        return only !== undefined
          ? portalText("operations", "value_is_outside_the_calling_hours_value_value_so_the_firs_6c3d587a", { v0: String(input.time), v1: clockOf(only.from), v2: clockOf(only.to), v3: String(waits) })
          : portalText("operations", "value_is_outside_that_day_s_calling_hours_value_so_the_fir_8a20d129", {
              v0: String(input.time),
              v1: dayRange.map((one) => `${clockOf(one.from)}–${clockOf(one.to)}`).join(", "),
              v2: String(waits),
            });
      },
      warning: true,
    };
  }
  const label = date.toLocaleDateString((portalLocale() === "el" ? "el-GR" : "en-GB"), { weekday: "long", day: "numeric", month: "long" });
  // plan-015 CMP (G17, answer 6): the design's sentence, true now — the start is each person's own, and the
  // campaign can be changed until it comes.
  return { get text() { return portalText("operations", "starts_value_at_value_in_each_person_s_local_time_editable_cd82373d", { v0: String(label), v1: String(input.time) }); }, warning: false };
}

// -------------------------------------------------------------------------------------------
// The end (plan-015 CMP, G16)
// -------------------------------------------------------------------------------------------

export interface EndNote {
  readonly text: string;
  readonly warning: boolean;
}

/** The end date's last calling minute on the member's clock, as the plan below reads it (the prototype's 23:59). */
function endMomentOf(endDate: Date): Date {
  return at(endDate, 23 * 60 + 59);
}

/**
 * Why the end date cannot be used, or `null` when it is empty or fine. An empty date is no end.
 */
export function endDateProblem(endDateText: string, startDateText: string, startedBy: Date | null = null): string | null {
  if (endDateText.trim() === "") return null;
  const end = dateOfDmy(endDateText);
  if (end === null) return portalText("operations", "write_the_end_date_as_dd_mm_yyyy_or_leave_it_empty_for_no_8a4ee832");
  const start = dateOfDmy(startDateText);
  if (start !== null && end.getTime() < start.getTime()) return portalText("operations", "the_end_date_is_before_the_start_pick_the_start_day_or_lat_f80c206a");
  // 2026-10-30: a campaign edited after it started ends today at the earliest (`startedBy` is now).
  if (startedBy !== null && end.getTime() < at(startedBy, 0).getTime()) return portalText("operations", "a_campaign_that_has_started_can_t_end_before_today_e816ef8e");
  return null;
}

/**
 * The design's sentence beside "Ends" (the prototype's `endNote`): with no end, until the last retry; with one,
 * whether everyone gets a first call and every retry before it — a retry that would fall after it is skipped.
 * Empty while there is no plan to compare with.
 */
export function endNoteOf(input: {
  readonly endDateText: string;
  readonly startDateText: string;
  readonly plan: CampaignPlan | null;
  /** 2026-10-30: now, when the campaign being edited has started; `null` otherwise. */
  readonly startedBy: Date | null;
}): EndNote {
  const problem = endDateProblem(input.endDateText, input.startDateText, input.startedBy);
  if (problem !== null) return { text: problem, warning: true };
  const plan = input.plan;
  if (plan === null) return { text: "", warning: false };
  const end = input.endDateText.trim() === "" ? null : dateOfDmy(input.endDateText);
  if (end === null) return { get text() { return portalText("operations", "runs_until_the_last_retry_about_value_e1151e28", { v0: String(dayTextOf(plan.lastRetry)) }); }, warning: false };
  const last = endMomentOf(end);
  if (plan.lastFirst.getTime() > last.getTime()) {
    return {
      get text() { return portalText("operations", "not_everyone_gets_a_first_call_before_value_raise_the_line_60964103", { v0: String(dayTextOf(end)) }); },
      warning: true,
    };
  }
  if (plan.lastRetry.getTime() > last.getTime()) {
    return {
      get text() { return portalText("operations", "everyone_gets_a_first_call_by_value_but_some_retries_would_ec9ad6db", { v0: String(dayTextOf(plan.lastFirst)), v1: String(dayTextOf(end)) }); },
      warning: true,
    };
  }
  return { get text() { return portalText("operations", "everyone_including_retries_is_done_by_value_da0f6bb4", { v0: String(dayTextOf(plan.lastRetry)) }); }, warning: false };
}

/** The estimate's last row (the prototype's `schedRows`): the last retry, or the end date when that comes first. */
export function lastDialDayOf(plan: CampaignPlan, endDateText: string): Date {
  const end = endDateText.trim() === "" ? null : dateOfDmy(endDateText);
  if (end === null) return plan.lastRetry;
  return plan.lastRetry.getTime() > endMomentOf(end).getTime() ? end : plan.lastRetry;
}

// -------------------------------------------------------------------------------------------
// The plan
// -------------------------------------------------------------------------------------------

export interface CampaignPlan {
  /** When the last person on the list gets their first call. */
  readonly lastFirst: Date;
  /** When the last retry would go out (equal to `lastFirst` with one attempt). */
  readonly lastRetry: Date;
}

/**
 * The first-call phase laid onto the calendar: `firstAttemptDayCount` calling days from the start, the
 * last first call half an hour before the day's calling hours end, then each retry after the wait,
 * moved to the next calling time when it falls outside one. `null` when there is no calling day or
 * the hours are not a window.
 */
export function campaignPlanOf(input: {
  readonly start: Date;
  /** 2026-10-30: the calling ranges per weekday. */
  readonly callingHours: readonly CallingRangeView[];
  readonly attemptsPerPerson: number;
  readonly waitMinute: number;
  readonly firstAttemptDayCount: number;
}): CampaignPlan | null {
  if (callingHoursProblemText(input.callingHours) !== null || input.firstAttemptDayCount < 1) return null;
  const rangesOn = (date: Date): readonly MinuteRange[] => rangesOfDay(input.callingHours, isoDayOf(date));

  // The next moment inside one of a calling day's ranges, at or after `moment` (two months at most).
  const snap = (moment: Date): Date | null => {
    let date = new Date(moment);
    for (let step = 0; step < 60; step += 1) {
      const minute = date.getHours() * 60 + date.getMinutes();
      for (const range of rangesOn(date)) {
        if (minute < range.from) return at(date, range.from);
        if (minute < range.to) return date;
      }
      date = new Date(date);
      date.setDate(date.getDate() + 1);
      date = at(date, 0);
    }
    return null;
  };

  let day = snap(input.start);
  for (let left = input.firstAttemptDayCount - 1; left > 0 && day !== null; left -= 1) {
    const next = new Date(day);
    next.setDate(next.getDate() + 1);
    day = snap(at(next, 0));
  }
  if (day === null) return null;
  // The last first call goes out half an hour before the day's last range ends.
  const last = rangesOn(day).at(-1);
  if (last === undefined) return null;
  const lastFirst = at(day, Math.max(last.from, last.to - 30));

  let lastRetry: Date | null = lastFirst;
  for (let attempt = 1; attempt < input.attemptsPerPerson && lastRetry !== null; attempt += 1) {
    lastRetry = snap(new Date(lastRetry.getTime() + input.waitMinute * 60_000));
  }
  return lastRetry === null ? null : { lastFirst, lastRetry };
}

// -------------------------------------------------------------------------------------------
// Questions
// -------------------------------------------------------------------------------------------

/** "2 scale · 1 yes/no or choice · 1 open" from the kind of each question. */
export function questionMixOf(kind: readonly ("scale" | "choice" | "open" | "number")[]): string {
  const count = (wanted: string): number => kind.filter((one) => one === wanted).length;
  return (
    [
      ["scale", portalText("operations", "campaign.mix.scale")],
      ["choice", portalText("operations", "yes_no_or_choice_520cd399")],
      ["open", portalText("operations", "campaign.mix.open")],
      ["number", portalText("operations", "campaign.mix.number")],
    ] as const
  )
    .map(([one, word]) => [count(one), word] as const)
    .filter(([n]) => n > 0)
    .map(([n, word]) => `${String(n)} ${word}`)
    .join(" · ");
}
